import type { ChannelChoice } from '../channel-selection.js';
import type { EpgRow } from '../epg.js';

/**
 * 홈의 **대상 채널** (v32 · `docs/63` §4.2).
 *
 * `resolveChannel` 의 결과(`play`·`list`·`empty`)를 **행**으로 바꾸되, 그 앞에 둘을 더 본다 —
 * `?channel=`(언제나 이긴다)과 **계정의 최근 시청**(로그인했을 때 · `lastWatchedChannelId`).
 * `list` 일 때 첫 행인 이유는 절대 규칙 71(순서는 번호 하나)이고, 그것이 곧 「1번을 누르면
 * 나오는 채널」이다. `rows` 는 `GET /v1/channels` 순서 그대로여야 한다 — 여기서 정렬하지 않는다.
 */
export function resolveWatchTarget(
  choice: ChannelChoice,
  rows: readonly EpgRow[],
  account: {
    /** `?channel=`·`VITE_CHANNEL_ID` — 하네스와 운영자의 고정 통로. **언제나 이긴다** */
    readonly preferred: string | null;
    /** `lastWatchedChannelId(rows)` — 로그인했을 때만. 기기의 저장값보다 **앞선다** */
    readonly lastWatched: string | null;
  } = { preferred: null, lastWatched: null },
): EpgRow | null {
  if (choice.kind === 'empty') return null;
  const find = (id: string | null) =>
    id === null ? null : (rows.find((row) => row.channel.channelId === id) ?? null);
  /*
   * ★ v32 — **계정이 기기를 이긴다** (2026-09-10 실기). 노트북에서 1번을 보다 폰에서 2번을 보고
   * 노트북을 다시 열면 홈은 2번이어야 한다. 근거는 `WatchProgress.updatedAt`(편성표의
   * `resumeUpdatedAt`)이고 기기의 `bloom.channelId` 는 그다음이다. `?channel=` 만 그보다 앞이다 —
   * 하네스와 운영자가 잠시 고정하는 통로라 계정 이력이 그것을 덮으면 안 된다.
   */
  const preferred = find(account.preferred);
  if (preferred !== null) return preferred;
  const last = find(account.lastWatched);
  if (last !== null) return last;
  if (choice.kind === 'list') return rows[0] ?? null;
  return find(choice.channelId);
}

/**
 * **계정이 가장 최근에 본 채널** — `resumeUpdatedAt` 이 가장 새 행. 없으면 `null`.
 *
 * `receiver` 채널 둘 다 후보다 — 홈쇼핑도 2026-09-10 부터 이력 행(콘텐츠 · 시각)을 갖는다. ONAIR 에는
 * 행이 없어 시각도 없다(규칙 76) — 그 채널의 마지막 시청만 계정 축에 남지 않는다.
 * 순수 함수다 — 시각 비교는 ISO 8601 문자열이라 사전순이 곧 시간순이다.
 */
export function lastWatchedChannelId(rows: readonly EpgRow[]): string | null {
  let best: { channelId: string; at: string } | null = null;
  for (const row of rows) {
    const at = row.lineup?.resumeUpdatedAt ?? null;
    if (at === null) continue;
    if (best === null || at > best.at) best = { channelId: row.channel.channelId, at };
  }
  return best?.channelId ?? null;
}

/**
 * masthead CTA 의 **뜻** — 행의 종류가 정한다 (`docs/63` §4.4 · 절대 규칙 68·70·76).
 *
 * | 행 | 뜻 |
 * |---|---|
 * | 일반 + 이어보기 지점 | `resume` — 「{time}부터 이어보기」 (`episodes.resumeAt`) |
 * | 일반 · 지점 없음 | `fromTop` — 처음이거나 비로그인(규칙 46) |
 * | 홈쇼핑 | `shopping` — 「지금」 + 상품명. 지점은 없고 시작 항목은 `toEpgRows` 가 이미 돌렸다 |
 * | ONAIR | `onair` — 「방송 중」 |
 * | 편성 응답이 아직 없다 | `pending` — 채널명만 |
 *
 * **홈은 지점을 넘기지 않는다.** 이 값은 문구를 고르는 근거이지 seek 의 근거가 아니다 —
 * 재생은 OK 뒤 리시버가 자기 요청으로 시작한다(D6).
 */
export type WatchNowLabel =
  | { readonly kind: 'pending' }
  | { readonly kind: 'resume'; readonly positionSec: number }
  | { readonly kind: 'fromTop' }
  | { readonly kind: 'shopping' }
  | { readonly kind: 'onair' };

export function watchNowLabel(row: EpgRow): WatchNowLabel {
  if (row.channel.delivery === 'onair') return { kind: 'onair' };
  if (row.lineup === null || row.lineup.items.length === 0) return { kind: 'pending' };
  if (row.channel.kind === 'homeshopping') return { kind: 'shopping' };
  const positionSec = row.lineup.resumePositionSec;
  return positionSec !== null && positionSec > 0
    ? { kind: 'resume', positionSec }
    : { kind: 'fromTop' };
}
